import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SETTINGS = {
  report_questions: [],
  feedback_questions: [],
  // Separate questions just for Backer Feedback
  feedback_backer_questions: [],
  staff_questions: [],
  // Separate questions just for Item Creation applications
  item_questions: [],
  lead_questions: [],
  admin_questions: [],
  // Sim VIP / Backers Waitlist application questions
  sim_vip_waitlist_questions: [],
  staff_disclaimer_text: "",
  lead_disclaimer_text: "",
  admin_disclaimer_text: "",
  perks_text: "",
  departments: [], // legacy
  staff_departments: [],
  admin_departments: [],
  organization_positions: [],
  // Discord routing
  global_test_channel_id: "",
  report_channels: [],
  // Per-role routing for staff reports
  report_role_routes: [],
  // Per-department routing for Staff applications
  staff_department_routes: [],
  feedback_hud_channels: [],
  feedback_general_channels: [],
  feedback_build_channels: [],
  feedback_backer_channels: [],
  application_staff_channels: [],
  application_item_channels: [],
  application_lead_channels: [],
  application_admin_channels: [],
};

function reorderItems(items, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function DragHandle({ testId, onDragStart, onDragEnd }) {
  return (
    <div
      draggable
      data-testid={testId}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="cursor-grab select-none rounded border border-amber-900/60 bg-black/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-300/80 active:cursor-grabbing"
      title="Drag to reorder"
    >
      ⋮⋮
    </div>
  );
}

function ChannelList({ title, description, value, onChange }) {
  const addChannel = () => {
    onChange([...(value || []), { id: "", label: "" }]);
  };

  const updateChannel = (idx, patch) => {
    const next = [...(value || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeChannel = (idx) => {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 space-y-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-amber-100 mb-1 text-sm">{title}</h3>
          <p className="text-amber-200/80 text-[11px]">{description}</p>
        </div>
        <button
          type="button"
          onClick={addChannel}
          className="inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
        >
          + Add Channel
        </button>
      </div>

      {(value || []).length === 0 && (
        <p className="text-amber-300/80 text-[11px]">No channels configured. Defaults will be used.</p>
      )}

      <div className="space-y-2 max-h-40 overflow-auto pr-1">
        {(value || []).map((ch, idx) => (
          <div
            key={idx}
            className="border border-amber-900/60 rounded-md p-2 space-y-1 bg-black/40 flex flex-col gap-1"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={ch.label || ""}
                onChange={(e) => updateChannel(idx, { label: e.target.value })}
                placeholder="Label (e.g. HUD Feedback, Builders)"
                className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
              />
              <input
                type="text"
                value={ch.id || ""}
                onChange={(e) => updateChannel(idx, { id: e.target.value })}
                placeholder="Channel ID"
                className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => removeChannel(idx)}
                className="px-2 py-0.5 rounded-full border border-red-700 text-red-300 hover:bg-red-900/40 text-[10px]"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionList({ title, description, questions, onChange }) {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const addQuestion = () => {
    const id = `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    onChange([
      ...questions,
      { id, label: "New question", type: "text", options: [], optionsText: "", required: false },
    ]);
  };

  const updateQuestion = (index, patch) => {
    const next = [...questions];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const removeQuestion = (index) => {
    const next = [...questions];
    next.splice(index, 1);
    onChange(next);
  };

  const moveQuestion = (targetIndex) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    onChange(reorderItems(questions, draggedIndex, targetIndex));
    setDraggedIndex(targetIndex);
  };

  return (
    <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 space-y-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-amber-100 mb-1 text-sm">{title}</h3>
          <p className="text-amber-200/80 text-[11px]">{description}</p>
        </div>
        <button
          type="button"
          onClick={addQuestion}
          className="inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
        >
          + Add Question
        </button>
      </div>

      {questions.length === 0 && (
        <p className="text-amber-300/80 text-[11px]">No questions configured yet.</p>
      )}

      <div className="space-y-2 max-h-60 overflow-auto pr-1">
        {questions.map((q, idx) => (
          <div
            key={q.id || idx}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => moveQuestion(idx)}
            className="border border-amber-900/60 rounded-md p-2 space-y-1 bg-black/40"
          >
            <div className="flex items-center gap-2">
              <DragHandle
                testId={`question-drag-handle-${q.id || idx}`}
                onDragStart={() => setDraggedIndex(idx)}
                onDragEnd={() => setDraggedIndex(null)}
              />
              <input
                data-testid={`question-label-input-${q.id || idx}`}
                type="text"
                value={q.label}
                onChange={(e) => updateQuestion(idx, { label: e.target.value })}
                placeholder="Question label"
                className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
              />
              <select
                data-testid={`question-type-select-${q.id || idx}`}
                value={q.type}
                onChange={(e) => updateQuestion(idx, { type: e.target.value })}
                className="bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
              >
                <option value="text">Text</option>
                <option value="dropdown">Dropdown</option>
                <option value="multi-select">Multi-select</option>
                <option value="checkbox">Checkbox</option>
              </select>
              <label className="inline-flex items-center gap-1 text-[11px] text-amber-200/80">
                <input
                  data-testid={`question-required-checkbox-${q.id || idx}`}
                  type="checkbox"
                  checked={!!q.required}
                  onChange={(e) => updateQuestion(idx, { required: e.target.checked })}
                  className="h-3 w-3"
                />
                <span>Required</span>
              </label>
              <button
                data-testid={`question-remove-button-${q.id || idx}`}
                type="button"
                onClick={() => removeQuestion(idx)}
                className="px-2 py-0.5 rounded-full border border-red-700 text-red-300 hover:bg-red-900/40 text-[10px]"
              >
                Remove
              </button>
            </div>

            {(q.type === "dropdown" || q.type === "multi-select") && (
              <div className="flex flex-col gap-1 mt-1">
                <span className="text-[11px] text-amber-200/80">Options (comma-separated)</span>
                <input
                  data-testid={`question-options-input-${q.id || idx}`}
                  type="text"
                  value={q.optionsText ?? (q.options || []).join(", ")}
                  onChange={(e) => {
                    const text = e.target.value;
                    updateQuestion(idx, {
                      optionsText: text,
                      options: text
                        .split(",")
                        .map((s) => s.trim())
                        .filter((s) => s.length > 0),
                    });
                  }}
                  className="bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminConfig() {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const [draggedStaffDepartmentIndex, setDraggedStaffDepartmentIndex] = useState(null);
  const [draggedAdminDepartmentIndex, setDraggedAdminDepartmentIndex] = useState(null);
  const [draggedOrganizationIndex, setDraggedOrganizationIndex] = useState(null);
  // Collapsible sections (reset on each load)
  const [showDiscordRouting, setShowDiscordRouting] = useState(false);
  const [showFeedbackQuestions, setShowFeedbackQuestions] = useState(false);
  const [showApplicationQuestions, setShowApplicationQuestions] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/howling_hills/admin/settings`);
      setSettings({ ...EMPTY_SETTINGS, ...(res.data || {}) });
    } catch (e) {
      console.error(e);
      setError("Failed to load Howling Hills settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const patch = {
        report_questions: settings.report_questions,
        feedback_questions: settings.feedback_questions,
        feedback_backer_questions: settings.feedback_backer_questions,
        staff_questions: settings.staff_questions,
        item_questions: settings.item_questions,
        lead_questions: settings.lead_questions,
        admin_questions: settings.admin_questions,
        sim_vip_waitlist_questions: settings.sim_vip_waitlist_questions,
        staff_disclaimer_text: settings.staff_disclaimer_text,
        lead_disclaimer_text: settings.lead_disclaimer_text,
        admin_disclaimer_text: settings.admin_disclaimer_text,
        perks_text: settings.perks_text,
        departments: settings.departments,
        staff_departments: settings.staff_departments,
        admin_departments: settings.admin_departments,
        organization_positions: settings.organization_positions,
        global_test_channel_id: settings.global_test_channel_id,
        report_channels: settings.report_channels,
        report_role_routes: settings.report_role_routes,
        staff_department_routes: settings.staff_department_routes,
        feedback_hud_channels: settings.feedback_hud_channels,
        feedback_general_channels: settings.feedback_general_channels,
        feedback_build_channels: settings.feedback_build_channels,
        feedback_backer_channels: settings.feedback_backer_channels,
        application_staff_channels: settings.application_staff_channels,
        application_item_channels: settings.application_item_channels,
        application_lead_channels: settings.application_lead_channels,
        application_admin_channels: settings.application_admin_channels,
        application_sim_vip_channels: settings.application_sim_vip_channels,
      };
      const res = await axios.patch(`${API}/howling_hills/admin/settings`, patch);
      setSettings({ ...EMPTY_SETTINGS, ...(res.data || {}) });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      console.error(e);
      setError("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const moveListItem = (field, fromIndex, toIndex, setDraggedIndex) => {
    if (fromIndex === null || fromIndex === toIndex) return;
    updateField(field, reorderItems(settings[field] || [], fromIndex, toIndex));
    setDraggedIndex(toIndex);
  };

  return (
    <section className="space-y-4 max-w-5xl">

          <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 text-xs text-amber-200/80 space-y-3">
            <button
              type="button"
              onClick={() => setShowDiscordRouting((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-amber-100 text-sm mb-0">Discord Routing</h3>
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-300/80">
                <span>{showDiscordRouting ? "Hide" : "Show"}</span>
                <span className="text-amber-300/80">{showDiscordRouting ? "▾" : "▸"}</span>
              </span>
            </button>

            {showDiscordRouting && (
              <>
                <p className="text-[11px] text-amber-200/80 mt-1">
                  Configure which Discord channels receive threads for each submission type. Owners will always be
                  notified. If you leave a row empty, the default routing is used.
                </p>

                <label className="space-y-1 block mt-2">
                  <span className="text-amber-200/80 text-[11px]">Global test channel ID (optional)</span>
                  <input
                    type="text"
                    value={settings.global_test_channel_id}
                    onChange={(e) => updateField("global_test_channel_id", e.target.value)}
                    placeholder="1290082519328362597 (send ALL Howling Hills traffic here while testing)"
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                  />
                  <p className="text-[10px] text-amber-300/80">
                    When set, every submission goes only to this channel. Clear to resume normal routing.
                  </p>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <ChannelList
                    title="Report a Staff Member"
                    description="Additional channels for staff reports (Owners always included)."
                    value={settings.report_channels}
                    onChange={(v) => updateField("report_channels", v)}
                  />
              <ChannelList
                title="HUD Feedback"
                description="Channels for HUD feedback submissions."
                value={settings.feedback_hud_channels}
                onChange={(v) => updateField("feedback_hud_channels", v)}
              />
              <ChannelList
                title="General Feedback"
                description="Channels for general feedback submissions."
                value={settings.feedback_general_channels}
                onChange={(v) => updateField("feedback_general_channels", v)}
              />
              <ChannelList
                title="Build Feedback"
                description="Channels for build feedback submissions."
                value={settings.feedback_build_channels}
                onChange={(v) => updateField("feedback_build_channels", v)}
              />
              <ChannelList
                title="Backer Feedback"
                description="Channels for backer feedback submissions."
                value={settings.feedback_backer_channels}
                onChange={(v) => updateField("feedback_backer_channels", v)}
              />
              <ChannelList
                title="Staff Applications"
                description="Channels for Staff Application submissions."
                value={settings.application_staff_channels}
                onChange={(v) => updateField("application_staff_channels", v)}
              />
              <ChannelList
                title="Item Creation Applications"
                description="Channels for Item Creation applications."
                value={settings.application_item_channels}
                onChange={(v) => updateField("application_item_channels", v)}
              />
              <ChannelList
                title="Lead Applications"
                description="Channels for Lead Application submissions."
                value={settings.application_lead_channels}
                onChange={(v) => updateField("application_lead_channels", v)}
              />
              <ChannelList
                title="Admin Applications"
                description="Channels for Admin Application submissions."
                value={settings.application_admin_channels}
                onChange={(v) => updateField("application_admin_channels", v)}
              />
              <ChannelList
                title="Sim VIP / Backers Waitlist Applications"
                description="Channels for Sim VIP / Backers Waitlist application submissions."
                value={settings.application_sim_vip_channels}
                onChange={(v) => updateField("application_sim_vip_channels", v)}
              />
            </div>

            {/* Role-based routing for staff reports */}
            <div className="grid grid-cols-1 gap-3 mt-3">
              <div className="rounded-xl border border-amber-900/70 bg-black/30 p-3 space-y-2 text-xs">
                <h4 className="font-semibold text-amber-100 text-sm">Report routing by Discord role</h4>
                <p className="text-[11px] text-amber-200/80 mb-1">
                  Map Discord role IDs to additional channels for staff reports. Any report about a staff member who has
                  one of these roles will also notify the configured channels (in addition to the base report routing
                  and Owners).
                </p>

                <div className="space-y-2 max-h-40 overflow-auto pr-1">
                  {(settings.report_role_routes || []).map((r, idx) => (
                    <div
                      key={r.role_id || idx}
                      className="border border-amber-900/60 rounded-md px-2 py-2 bg-black/30 space-y-2"
                    >
                      <div className="flex flex-col md:flex-row gap-2">
                        <input
                          type="text"
                          value={r.role_id || ""}
                          onChange={(e) => {
                            const next = [...(settings.report_role_routes || [])];
                            next[idx] = { ...next[idx], role_id: e.target.value };
                            updateField("report_role_routes", next);
                          }}
                          placeholder="Discord role ID (e.g. 1234567890)"
                          className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                        />
                        <input
                          type="text"
                          value={r.label || ""}
                          onChange={(e) => {
                            const next = [...(settings.report_role_routes || [])];
                            next[idx] = { ...next[idx], label: e.target.value };
                            updateField("report_role_routes", next);
                          }}
                          placeholder="Label (e.g. Events Staff)"
                          className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                        />
                      </div>

                      <div className="pl-1 space-y-1">
                        <p className="text-[11px] text-amber-200/80">Channels for this role</p>
                        <div className="space-y-1">
                          {(r.channels || []).map((ch, cIdx) => (
                            <div
                              key={cIdx}
                              className="flex flex-col md:flex-row gap-2 border border-amber-900/50 rounded px-2 py-1 bg-black/40"
                            >
                              <input
                                type="text"
                                value={ch.label || ""}
                                onChange={(e) => {
                                  const next = [...(settings.report_role_routes || [])];
                                  const channels = [...(next[idx].channels || [])];
                                  channels[cIdx] = { ...channels[cIdx], label: e.target.value };
                                  next[idx] = { ...next[idx], channels };
                                  updateField("report_role_routes", next);
                                }}
                                placeholder="Channel label (e.g. Events Leads)"
                                className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                              />
                              <input
                                type="text"
                                value={ch.id || ""}
                                onChange={(e) => {
                                  const next = [...(settings.report_role_routes || [])];
                                  const channels = [...(next[idx].channels || [])];
                                  channels[cIdx] = { ...channels[cIdx], id: e.target.value };
                                  next[idx] = { ...next[idx], channels };
                                  updateField("report_role_routes", next);
                                }}
                                placeholder="Channel ID"
                                className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                              />
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = [...(settings.report_role_routes || [])];
                                    const channels = [...(next[idx].channels || [])];
                                    channels.splice(cIdx, 1);
                                    next[idx] = { ...next[idx], channels };
                                    updateField("report_role_routes", next);
                                  }}
                                  className="px-2 py-0.5 rounded-full border border-red-700 text-red-300 hover:bg-red-900/40 text-[10px]"
                                >
                                  Remove channel
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const next = [...(settings.report_role_routes || [])];
                              const channels = [...(next[idx].channels || [])];
                              channels.push({ id: "", label: "" });
                              next[idx] = { ...next[idx], channels };
                              updateField("report_role_routes", next);
                            }}
                            className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
                          >
                            + Add Channel for Role
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...(settings.report_role_routes || [])];
                            next.splice(idx, 1);
                            updateField("report_role_routes", next);
                          }}
                          className="px-2 py-0.5 rounded-full border border-red-700 text-red-300 hover:bg-red-900/40 text-[10px] mt-1"
                        >
                          Remove role route
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      updateField("report_role_routes", [
                        ...(settings.report_role_routes || []),
                        { role_id: "", label: "", channels: [] },
                      ])
                    }
                    className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
                  >
                    + Add role route
                  </button>
                </div>
              </div>

              {/* Department-based routing for Staff Applications */}
              <div className="rounded-xl border border-amber-900/70 bg-black/30 p-3 space-y-2 text-xs">
                <h4 className="font-semibold text-amber-100 text-sm">Staff Application routing by department</h4>
                <p className="text-[11px] text-amber-200/80 mb-1">
                  Configure routing for each Staff Application department. Each department listed below matches a
                  department that applicants can select on the Staff Application form.
                </p>

                <div className="space-y-2 max-h-40 overflow-auto pr-1">
                  {(settings.staff_departments || []).map((dept, idx) => {
                    const routes = settings.staff_department_routes || [];
                    const existing = routes.find((r) => r.department_id === dept.id) || {
                      department_id: dept.id,
                      label: dept.name || "",
                      channels: [],
                    };
                    const channels = existing.channels || [];

                    const updateRoute = (nextChannels) => {
                      const nextRoutes = [...routes];
                      const foundIdx = nextRoutes.findIndex((r) => r.department_id === dept.id);
                      if (foundIdx === -1) {
                        nextRoutes.push({ ...existing, channels: nextChannels });
                      } else {
                        nextRoutes[foundIdx] = { ...nextRoutes[foundIdx], channels: nextChannels };
                      }
                      updateField("staff_department_routes", nextRoutes);
                    };

                    return (
                      <div
                        key={dept.id || idx}
                        className="border border-amber-900/60 rounded-md px-2 py-2 bg-black/30 space-y-2"
                      >
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 justify-between">
                          <div className="flex-1">
                            <p className="text-[11px] text-amber-100 font-semibold">
                              {dept.name || "(unnamed department)"}
                            </p>
                            <p className="text-[10px] text-amber-300/80 break-all">
                              ID: {dept.id || "(no id set)"}
                            </p>
                          </div>
                          <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-800 text-amber-200/80">
                            {dept.open === false ? "Closed" : "Open"}
                          </span>
                        </div>

                        <div className="pl-1 space-y-1">
                          <p className="text-[11px] text-amber-200/80">Channels for this department</p>
                          <div className="space-y-1">
                            {channels.map((ch, cIdx) => (
                              <div
                                key={cIdx}
                                className="flex flex-col md:flex-row gap-2 border border-amber-900/50 rounded px-2 py-1 bg-black/40"
                              >
                                <input
                                  type="text"
                                  value={ch.label || ""}
                                  onChange={(e) => {
                                    const nextChannels = [...channels];
                                    nextChannels[cIdx] = { ...nextChannels[cIdx], label: e.target.value };
                                    updateRoute(nextChannels);
                                  }}
                                  placeholder="Channel label (e.g. Events Admin Chat)"
                                  className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                                />
                                <input
                                  type="text"
                                  value={ch.id || ""}
                                  onChange={(e) => {
                                    const nextChannels = [...channels];
                                    nextChannels[cIdx] = { ...nextChannels[cIdx], id: e.target.value };
                                    updateRoute(nextChannels);
                                  }}
                                  placeholder="Channel ID"
                                  className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
                                />
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const nextChannels = [...channels];
                                      nextChannels.splice(cIdx, 1);
                                      updateRoute(nextChannels);
                                    }}
                                    className="px-2 py-0.5 rounded-full border border-red-700 text-red-300 hover:bg-red-900/40 text-[10px]"
                                  >
                                    Remove channel
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const nextChannels = [...channels, { id: "", label: "" }];
                                updateRoute(nextChannels);
                              }}
                              className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
                            >
                              + Add Channel for Department
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

              </>
            )}
          </div>

      <header>
        <h2 className="text-2xl font-semibold text-amber-100 mb-1">Howling Hills  Admin Configuration</h2>
        <p className="text-sm text-amber-200/80">
          Configure questions, disclaimers, perks, and department availability for all Howling Hills forms.
        </p>
        {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
        {savedAt && !error && (
          <p className="text-xs text-emerald-300 mt-1">Saved at {savedAt}</p>
        )}
      </header>

      {loading ? (
        <p className="text-sm text-amber-200/80">Loading settings</p>
      ) : (
        <>
          <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 text-xs text-amber-200/80 space-y-2">
            <button
              type="button"
              onClick={() => setShowFeedbackQuestions((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-amber-100 text-sm mb-0">Feedback Questions</h3>
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-300/80">
                <span>{showFeedbackQuestions ? "Hide" : "Show"}</span>
                <span className="text-amber-300/80">{showFeedbackQuestions ? "▾" : "▸"}</span>
              </span>
            </button>

            {showFeedbackQuestions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-2">
                <QuestionList
                  title="Report a Staff Member"
                  description="Configure additional questions for staff reports. These are shown after the staff selector."
                  questions={settings.report_questions}
                  onChange={(qs) => updateField("report_questions", qs)}
                />

                <QuestionList
                  title="Feedback Forms"
                  description="Configure HUD, general, and build feedback questions (shared)."
                  questions={settings.feedback_questions}
                  onChange={(qs) => updateField("feedback_questions", qs)}
                />

                <QuestionList
                  title="Backer Feedback"
                  description="Configure questions used only for Backer Feedback submissions."
                  questions={settings.feedback_backer_questions}
                  onChange={(qs) => updateField("feedback_backer_questions", qs)}
                />
              </div>
            )}
          </div>

          <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 text-xs text-amber-200/80 space-y-2">
            <button
              type="button"
              onClick={() => setShowApplicationQuestions((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-amber-100 text-sm mb-0">Application Questions</h3>
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-300/80">
                <span>{showApplicationQuestions ? "Hide" : "Show"}</span>
                <span className="text-amber-300/80">{showApplicationQuestions ? "▾" : "▸"}</span>
              </span>
            </button>

            {showApplicationQuestions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-2">
                <QuestionList
                  title="Staff Application"
                  description="Configure questions for Staff applications (in addition to shared fields and disclaimers)."
                  questions={settings.staff_questions}
                  onChange={(qs) => updateField("staff_questions", qs)}
                />

                <QuestionList
                  title="Item Creation Application"
                  description="Configure questions used only for Item Creation applications."
                  questions={settings.item_questions}
                  onChange={(qs) => updateField("item_questions", qs)}
                />

                <QuestionList
                  title="Lead Application"
                  description="Configure questions for Lead applications."
                  questions={settings.lead_questions}
                  onChange={(qs) => updateField("lead_questions", qs)}
                />

                <QuestionList
                  title="Admin Application"
                  description="Configure questions for Admin applications (departments only; no staff position selector)."
                  questions={settings.admin_questions}
                  onChange={(qs) => updateField("admin_questions", qs)}
                />

                <QuestionList
                  title="Sim VIP / Backers Waitlist Application"
                  description="Configure questions for Sim VIP / Backers Waitlist applications."
                  questions={settings.sim_vip_waitlist_questions}
                  onChange={(qs) => updateField("sim_vip_waitlist_questions", qs)}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 space-y-2">
              <h3 className="font-semibold text-amber-100 text-sm">Staff Disclaimer &amp; Perks</h3>
              <label className="space-y-1 block">
                <span className="text-amber-200/80 text-[11px]">Staff disclaimer text</span>
                <textarea
                  rows={4}
                  value={settings.staff_disclaimer_text}
                  onChange={(e) => updateField("staff_disclaimer_text", e.target.value)}
                  className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px] leading-[1.6] resize-vertical"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-amber-200/80 text-[11px]">Perks section text</span>
                <textarea
                  rows={3}
                  value={settings.perks_text}
                  onChange={(e) => updateField("perks_text", e.target.value)}
                  className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px] resize-vertical"
                />
              </label>
            </div>

            <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 space-y-2">
              <h3 className="font-semibold text-amber-100 text-sm">Lead &amp; Admin Disclaimers</h3>
              <label className="space-y-1 block">
                <span className="text-amber-200/80 text-[11px]">Lead application disclaimer</span>
                <textarea
                  rows={3}
                  value={settings.lead_disclaimer_text}
                  onChange={(e) => updateField("lead_disclaimer_text", e.target.value)}
                  className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px] leading-[1.6] resize-vertical"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-amber-200/80 text-[11px]">Admin application disclaimer</span>
                <textarea
                  rows={3}
                  value={settings.admin_disclaimer_text}
                  onChange={(e) => updateField("admin_disclaimer_text", e.target.value)}
                  className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px] leading-[1.6] resize-vertical"
                />
              </label>
            </div>
          </div>

          <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 text-xs text-amber-200/80 space-y-3">
            <h3 className="font-semibold text-amber-100 text-sm mb-1">Staff Departments &amp; Positions</h3>
            <p className="text-[11px] text-amber-200/80 mb-1">
              Manage departments and staff positions for Staff applications. Each department can have multiple
              positions (e.g. Mentor, Moderator, Historian, DM) and independent open/closed state.
            </p>
            <div className="space-y-2 max-h-56 overflow-auto pr-1">
              {(settings.staff_departments || []).map((d, idx) => (
                <div
                  key={d.id || idx}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => moveListItem("staff_departments", draggedStaffDepartmentIndex, idx, setDraggedStaffDepartmentIndex)}
                  className="border border-amber-900/60 rounded-md px-2 py-2 bg-black/30 space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      <DragHandle
                        testId={`staff-department-drag-handle-${idx}`}
                        onDragStart={() => setDraggedStaffDepartmentIndex(idx)}
                        onDragEnd={() => setDraggedStaffDepartmentIndex(null)}
                      />
                      <div className="flex-1 space-y-0.5">
                        <input
                          data-testid={`staff-department-name-input-${idx}`}
                          type="text"
                          value={d.name || ""}
                          onChange={(e) => {
                            const next = [...(settings.staff_departments || [])];
                            next[idx] = { ...next[idx], name: e.target.value };
                            updateField("staff_departments", next);
                          }}
                          placeholder="Department name (e.g. Events, Faction)"
                          className="flex-1 bg-transparent border-none text-amber-100 text-[11px] focus:outline-none"
                        />
                        <p className="text-[10px] text-amber-300/80 flex items-center gap-1">
                          <span className="text-amber-400">#</span>
                          <span>{d.id || "(no id set)"}</span>
                        </p>
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-1 text-[11px] text-amber-200/80 whitespace-nowrap">
                      <input
                        data-testid={`staff-department-open-checkbox-${idx}`}
                        type="checkbox"
                        checked={d.open !== false}
                        onChange={(e) => {
                          const next = [...(settings.staff_departments || [])];
                          next[idx] = { ...next[idx], open: e.target.checked };
                          updateField("staff_departments", next);
                        }}
                        className="h-3 w-3"
                      />
                      <span>{d.open === false ? "Closed" : "Open"}</span>
                    </label>
                  </div>

                  <div className="pl-1 space-y-1">
                    <p className="text-[11px] text-amber-200/80">Positions</p>
                    <div className="space-y-1">
                      {(d.positions || []).map((p, pIdx) => (
                        <label
                          key={p.id || pIdx}
                          className="flex items-center justify-between gap-2 border border-amber-900/50 rounded px-2 py-1 bg-black/40"
                        >
                          <input
                            type="text"
                            value={p.name || ""}
                            onChange={(e) => {
                              const next = [...(settings.staff_departments || [])];
                              const positions = [...(next[idx].positions || [])];
                              positions[pIdx] = { ...positions[pIdx], name: e.target.value };
                              next[idx] = { ...next[idx], positions };
                              updateField("staff_departments", next);
                            }}
                            placeholder="Position name (e.g. Moderator, Mentor)"
                            className="flex-1 bg-transparent border-none text-amber-100 text-[11px] focus:outline-none"
                          />
                          <label className="inline-flex items-center gap-1 text-[11px] text-amber-200/80 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={p.open !== false}
                              onChange={(e) => {
                                const next = [...(settings.staff_departments || [])];
                                const positions = [...(next[idx].positions || [])];
                                positions[pIdx] = { ...positions[pIdx], open: e.target.checked };
                                next[idx] = { ...next[idx], positions };
                                updateField("staff_departments", next);
                              }}
                              className="h-3 w-3"
                            />
                            <span>{p.open === false ? "Closed" : "Open"}</span>
                          </label>
                        </label>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...(settings.staff_departments || [])];
                          const positions = [...(next[idx].positions || [])];
                          positions.push({ id: `pos_${Date.now()}`, name: "New Position", open: true });
                          next[idx] = { ...next[idx], positions };
                          updateField("staff_departments", next);
                        }}
                        className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
                      >
                        + Add Position
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  updateField("staff_departments", [
                    ...(settings.staff_departments || []),
                    { id: `dept_${Date.now()}`, name: "New Department", open: true, positions: [] },
                  ])
                }
                className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
              >
                + Add Department
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 text-xs text-amber-200/80 space-y-2">
            <h3 className="font-semibold text-amber-100 text-sm mb-1">Admin Departments</h3>
            <p className="text-[11px] text-amber-200/80 mb-1">
              Manage departments for Admin applications. These are separate from Staff departments and can be
              opened/closed independently.
            </p>
            <div className="space-y-1 max-h-40 overflow-auto pr-1">
              {(settings.admin_departments || []).map((d, idx) => (
                <div
                  key={d.id || idx}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => moveListItem("admin_departments", draggedAdminDepartmentIndex, idx, setDraggedAdminDepartmentIndex)}
                  className="flex items-center justify-between gap-2 border border-amber-900/50 rounded px-2 py-1 bg-black/30"
                >
                  <div className="flex items-start gap-2 flex-1">
                    <DragHandle
                      testId={`admin-department-drag-handle-${idx}`}
                      onDragStart={() => setDraggedAdminDepartmentIndex(idx)}
                      onDragEnd={() => setDraggedAdminDepartmentIndex(null)}
                    />
                    <div className="flex-1 space-y-0.5">
                      <input
                        data-testid={`admin-department-name-input-${idx}`}
                        type="text"
                        value={d.name || ""}
                        onChange={(e) => {
                          const next = [...(settings.admin_departments || [])];
                          next[idx] = { ...next[idx], name: e.target.value };
                          updateField("admin_departments", next);
                        }}
                        placeholder="Admin department (e.g. Events Admin)"
                        className="w-full bg-transparent border-none text-amber-100 text-[11px] focus:outline-none"
                      />
                      <p className="text-[10px] text-amber-300/80 flex items-center gap-1">
                        <span className="text-amber-400">#</span>
                        <span>{d.id || "(no id set)"}</span>
                      </p>
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-1 text-[11px] text-amber-200/80 whitespace-nowrap">
                    <input
                      data-testid={`admin-department-open-checkbox-${idx}`}
                      type="checkbox"
                      checked={d.open !== false}
                      onChange={(e) => {
                        const next = [...(settings.admin_departments || [])];
                        next[idx] = { ...next[idx], open: e.target.checked };
                        updateField("admin_departments", next);
                      }}
                      className="h-3 w-3"
                    />
                    <span>{d.open === false ? "Closed" : "Open"}</span>
                  </label>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  updateField("admin_departments", [
                    ...(settings.admin_departments || []),
                    { id: `adm_${Date.now()}`, name: "New Admin Department", open: true },
                  ])
                }
                className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
              >
                + Add Admin Department
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-amber-900/70 bg-black/30 p-4 text-xs text-amber-200/80 space-y-2">
            <h3 className="font-semibold text-amber-100 text-sm mb-1">Organization Positions</h3>
            <p className="text-[11px] text-amber-200/80 mb-1">
              Manage organizations for Lead applications. Closed organizations still appear on the form, but are
              disabled for applicants.
            </p>
            <div className="space-y-1 max-h-40 overflow-auto pr-1">
              {(settings.organization_positions || []).map((org, idx) => (
                <div
                  key={org.id || idx}
                  data-testid={`organization-position-row-${idx}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => moveListItem("organization_positions", draggedOrganizationIndex, idx, setDraggedOrganizationIndex)}
                  className="flex items-center justify-between gap-2 border border-amber-900/50 rounded px-2 py-1 bg-black/30"
                >
                  <div className="flex items-start gap-2 flex-1">
                    <DragHandle
                      testId={`organization-position-drag-handle-${idx}`}
                      onDragStart={() => setDraggedOrganizationIndex(idx)}
                      onDragEnd={() => setDraggedOrganizationIndex(null)}
                    />
                    <div className="flex-1 space-y-0.5">
                      <input
                        data-testid={`organization-position-name-input-${idx}`}
                        type="text"
                        value={org.name || ""}
                        onChange={(e) => {
                          const next = [...(settings.organization_positions || [])];
                          next[idx] = { ...next[idx], name: e.target.value };
                          updateField("organization_positions", next);
                        }}
                        placeholder="Organization name (e.g. RDA)"
                        className="w-full bg-transparent border-none text-amber-100 text-[11px] focus:outline-none"
                      />
                      <p
                        data-testid={`organization-position-id-${idx}`}
                        className="text-[10px] text-amber-300/80 flex items-center gap-1"
                      >
                        <span className="text-amber-400">#</span>
                        <span>{org.id || "(no id set)"}</span>
                      </p>
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-1 text-[11px] text-amber-200/80 whitespace-nowrap">
                    <input
                      data-testid={`organization-position-open-checkbox-${idx}`}
                      type="checkbox"
                      checked={org.open !== false}
                      onChange={(e) => {
                        const next = [...(settings.organization_positions || [])];
                        next[idx] = { ...next[idx], open: e.target.checked };
                        updateField("organization_positions", next);
                      }}
                      className="h-3 w-3"
                    />
                    <span>{org.open === false ? "Closed" : "Open"}</span>
                  </label>
                </div>
              ))}
              <button
                data-testid="add-organization-position-button"
                type="button"
                onClick={() =>
                  updateField("organization_positions", [
                    ...(settings.organization_positions || []),
                    { id: `org_${Date.now()}`, name: "New Organization", open: true },
                  ])
                }
                className="mt-1 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-[11px] font-semibold"
              >
                + Add Organization
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-black px-4 py-1.5 text-xs font-semibold"
            >
              {saving ? "Saving..." : "Save settings"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
